import {
  type FC,
  useMemo,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import AppLayout from '@/components/feature/AppLayout';

import {
  LoadingState,
} from '@/components/base';

import {
  useAttentionCenter,
} from '@/hooks/useAttentionCenter';

import {
  useDailyBrief,
} from '@/hooks/useDailyBrief';

import {
  buildDailyBriefHeadline,
  buildDailyBriefTone,
} from '@/lib/dailyBrief';

const localDateKey = () => {
  const now =
    new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() +
      1
    ).padStart(
      2,
      '0'
    );

  const day =
    String(
      now.getDate()
    ).padStart(
      2,
      '0'
    );

  return `${year}-${month}-${day}`;
};

const sameDate = (
  value:
    string |
    null,

  date:
    string
) => {
  if (!value) {
    return false;
  }

  const raw =
    value.slice(
      0,
      10
    );

  return raw ===
    date;
};

const formatTime = (
  value:
    string |
    null
) => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(
      value
    );

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return parsed
    .toLocaleTimeString(
      'pt-BR',
      {
        hour:
          '2-digit',

        minute:
          '2-digit',
      }
    );
};

const formatDateTime = (
  value:
    string |
    null
) => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(
      value
    );

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return parsed
    .toLocaleString(
      'pt-BR',
      {
        day:
          '2-digit',

        month:
          '2-digit',

        hour:
          '2-digit',

        minute:
          '2-digit',
      }
    );
};

const money = (
  value:
    number
) =>
  new Intl
    .NumberFormat(
      'pt-BR',
      {
        style:
          'currency',

        currency:
          'BRL',
      }
    )
    .format(
      value
    );

const TodayPage:
  FC = () => {
  const navigate =
    useNavigate();

  const date =
    useMemo(
      localDateKey,
      []
    );

  const {
    data,
    loading,
    error,
    load,
  } =
    useDailyBrief(
      date
    );

  const {
    items:
      attentionItems,

    summary:
      attentionSummary,

    loading:
      attentionLoading,

    error:
      attentionError,

    aggregation:
      attentionAggregation,

    load:
      loadAttention,
  } =
    useAttentionCenter({
      limit:
        100,

    });

  const deadlinesToday =
    useMemo(
      () =>
        attentionItems.filter(
          (
            item
          ) =>
            item.source ===
              'deadline' &&
            sameDate(
              item.dueAt,
              date
            )
        ),
      [
        attentionItems,
        date,
      ]
    );

  const tasksToday =
    useMemo(
      () =>
        attentionItems.filter(
          (
            item
          ) =>
            item.source ===
              'task' &&
            sameDate(
              item.dueAt,
              date
            )
        ),
      [
        attentionItems,
        date,
      ]
    );

  const priorityItems =
    useMemo(
      () =>
        attentionItems.slice(
          0,
          6
        ),
      [
        attentionItems,
      ]
    );

  const headline =
    buildDailyBriefHeadline({
      criticalAttention:
        attentionSummary
          .critical,

      importantAttention:
        attentionSummary
          .important,

      eventsToday:
        data
          ?.schedule
          .length ??
        0,

      deadlinesToday:
        deadlinesToday
          .length,

      tasksToday:
        tasksToday
          .length,

      followupsDue:
        data
          ?.followups
          .length ??
        0,

      installmentsDue:
        data
          ?.installments
          .length ??
        0,
    });

  const tone =
    buildDailyBriefTone(
      attentionSummary
        .critical,

      attentionSummary
        .important
    );

  const toneClass =
    tone ===
      'critical'
      ? 'border-red-200 bg-red-50 text-red-800'
      : tone ===
          'important'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800';

  const refresh =
    async () => {
      await Promise.all([
        load(),
        loadAttention(),
      ]);
    };

  if (
    loading &&
    !data
  ) {
    return (
      <AppLayout>
        <div className="p-6">
          <LoadingState message="Montando a visão do dia..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-primary-600 font-semibold">
              Daily Brief
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-1">
              Hoje
            </h2>

            <p className="text-sm text-foreground-500 mt-0.5">
              {new Date(
                `${date}T12:00:00`
              ).toLocaleDateString(
                'pt-BR',
                {
                  weekday:
                    'long',

                  day:
                    '2-digit',

                  month:
                    'long',

                  year:
                    'numeric',
                }
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] text-foreground-400">
              {data?.generatedAt
                ? `Atualizado ${formatDateTime(
                    data.generatedAt
                  )}`
                : 'Atualizando'}
            </span>

            <button
              type="button"
              onClick={() => void refresh()}
              className="px-3 py-2 rounded-lg border border-background-200 text-xs font-semibold text-foreground-600 hover:bg-background-100 transition-colors"
            >
              <i className="ri-refresh-line mr-1"></i>
              Atualizar
            </button>
          </div>
        </div>

        {(error || attentionError) && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {[error, attentionError]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}

        {((data && !data.aggregation.complete) || !attentionAggregation.complete) && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Não foi possível consolidar todas as fontes. A visão pode estar parcial ou desatualizada.
          </div>
        )}

        <div className={`rounded-2xl border p-5 ${toneClass}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0">
              <i className={
                tone === 'critical'
                  ? 'ri-alarm-warning-line text-xl'
                  : tone === 'important'
                    ? 'ri-alert-line text-xl'
                    : 'ri-sun-line text-xl'
              }></i>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
                Prioridade do dia
              </p>

              <p className="text-lg font-bold mt-1">
                {headline}
              </p>

              <p className="text-xs mt-1 opacity-80">
                A visão é determinística e usa os dados reais do BM Legal; não depende de API de IA.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <button
            type="button"
            onClick={() => navigate('/attention')}
            className="text-left rounded-xl border border-red-200 bg-red-50 p-4 hover:shadow-sm transition-shadow"
          >
            <p className="text-xs text-red-600">Críticos</p>
            <p className="text-2xl font-bold text-red-700 mt-1">
              {attentionSummary.critical}
            </p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/deadlines')}
            className="text-left rounded-xl border border-background-200 bg-white p-4 hover:border-primary-200 transition-colors"
          >
            <p className="text-xs text-foreground-500">Prazos hoje</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">
              {deadlinesToday.length}
            </p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/tasks')}
            className="text-left rounded-xl border border-background-200 bg-white p-4 hover:border-primary-200 transition-colors"
          >
            <p className="text-xs text-foreground-500">Tarefas hoje</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">
              {tasksToday.length}
            </p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/agenda')}
            className="text-left rounded-xl border border-background-200 bg-white p-4 hover:border-primary-200 transition-colors"
          >
            <p className="text-xs text-foreground-500">Agenda</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">
              {data?.schedule.length ?? 0}
            </p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/leads')}
            className="text-left rounded-xl border border-background-200 bg-white p-4 hover:border-primary-200 transition-colors"
          >
            <p className="text-xs text-foreground-500">Follow-ups</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">
              {data?.followups.length ?? 0}
            </p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/finance')}
            className="text-left rounded-xl border border-background-200 bg-white p-4 hover:border-primary-200 transition-colors"
          >
            <p className="text-xs text-foreground-500">Vence hoje</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">
              {data?.installments.length ?? 0}
            </p>
          </button>
        </div>

        <div className="grid xl:grid-cols-2 gap-5">
          <section className="rounded-2xl border border-background-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  Prioridades
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Primeiros itens da Central de Atenção
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/attention')}
                className="text-xs font-medium text-primary-600"
              >
                Ver tudo
              </button>
            </div>

            <div className="divide-y divide-background-100">
              {attentionLoading && priorityItems.length === 0 ? (
                <div className="p-5 text-xs text-foreground-500">
                  Carregando prioridades...
                </div>
              ) : priorityItems.length === 0 && data?.aggregation.complete !== false && attentionAggregation.complete ? (
                <div className="p-5 text-sm text-foreground-500">
                  Nenhuma pendência prioritária.
                </div>
              ) : priorityItems.length === 0 ? (
                <div className="p-5 text-sm text-amber-700">A consolidação está incompleta; não é possível afirmar que não há pendências.</div>
              ) : (
                priorityItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(item.href)}
                    className="w-full text-left px-5 py-3 hover:bg-background-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 line-clamp-1">
                          {item.title}
                        </p>

                        {item.detail && (
                          <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">
                            {item.detail}
                          </p>
                        )}
                      </div>

                      <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${
                        item.severity === 'critical'
                          ? 'bg-red-50 text-red-700'
                          : item.severity === 'important'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-blue-50 text-blue-700'
                      }`}>
                        {item.severity === 'critical'
                          ? 'Crítico'
                          : item.severity === 'important'
                            ? 'Importante'
                            : 'Acompanhar'}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-background-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  Agenda de hoje
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Compromissos da agenda interna
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/agenda')}
                className="text-xs font-medium text-primary-600"
              >
                Abrir agenda
              </button>
            </div>

            <div className="divide-y divide-background-100">
              {!data?.schedule.length ? (
                <div className="p-5 text-sm text-foreground-500">
                  Nenhum compromisso cadastrado para hoje.
                </div>
              ) : (
                data.schedule.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => navigate('/agenda')}
                    className="w-full text-left px-5 py-3 hover:bg-background-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-14 flex-shrink-0">
                        <p className="text-sm font-bold text-primary-700">
                          {formatTime(event.startAt)}
                        </p>
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800">
                          {event.title}
                        </p>

                        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">
                          {[
                            event.caseTitle,
                            event.clientName,
                            event.leadName,
                            event.location,
                          ]
                            .filter(Boolean)
                            .join(' · ') || 'Compromisso interno'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>
        </div>

        <div className="grid xl:grid-cols-3 gap-5">
          <section className="rounded-2xl border border-background-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  Casos
                </h3>

                <p className="text-xs text-foreground-500">
                  Estado operacional atual
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/cases')}
                className="text-xs font-medium text-primary-600"
              >
                Abrir
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-500">Ativos</span>
                <strong>{data?.cases.active ?? 0}</strong>
              </div>

              <div className="flex justify-between">
                <span className="text-foreground-500">Aguardando cliente</span>
                <strong>{data?.cases.waitingClient ?? 0}</strong>
              </div>

              <div className="flex justify-between">
                <span className="text-foreground-500">Aguardando juízo</span>
                <strong>{data?.cases.waitingCourt ?? 0}</strong>
              </div>

              <div className="flex justify-between">
                <span className="text-foreground-500">Audiência agendada</span>
                <strong>{data?.cases.hearingScheduled ?? 0}</strong>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-background-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  Comunicação
                </h3>

                <p className="text-xs text-foreground-500">
                  Entrada do dia
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/messages')}
                className="text-xs font-medium text-primary-600"
              >
                Abrir
              </button>
            </div>

            <p className="text-3xl font-bold text-foreground-900">
              {data?.communications.inboundToday ?? 0}
            </p>

            <p className="text-xs text-foreground-500 mt-1">
              comunicação(ões) recebida(s) hoje
            </p>

            <p className="text-xs text-foreground-400 mt-4">
              Gmail e WhatsApp só entram quando as respectivas integrações estiverem homologadas.
            </p>
          </section>

          <section className="rounded-2xl border border-background-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  Financeiro de hoje
                </h3>

                <p className="text-xs text-foreground-500">
                  Parcelas com vencimento na data
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/finance')}
                className="text-xs font-medium text-primary-600"
              >
                Abrir
              </button>
            </div>

            <p className="text-3xl font-bold text-foreground-900">
              {money(
                data?.installments.reduce(
                  (
                    total,
                    item
                  ) =>
                    total +
                    item.outstanding,
                  0
                ) ??
                0
              )}
            </p>

            <p className="text-xs text-foreground-500 mt-1">
              saldo previsto para vencer hoje
            </p>
          </section>
        </div>

        {(data?.followups.length ?? 0) > 0 && (
          <section className="rounded-2xl border border-background-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  Follow-ups vencidos ou de hoje
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Leads que precisam de novo contato
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/leads')}
                className="text-xs font-medium text-primary-600"
              >
                Abrir CRM
              </button>
            </div>

            <div className="divide-y divide-background-100">
              {data?.followups.slice(0, 8).map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => navigate('/leads')}
                  className="w-full text-left px-5 py-3 hover:bg-background-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-800">
                        {lead.name}
                      </p>

                      <p className="text-xs text-foreground-500 mt-0.5">
                        {lead.legalProductName || lead.status}
                      </p>
                    </div>

                    <span className="text-[11px] text-foreground-400 flex-shrink-0">
                      {formatDateTime(lead.nextContactAt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppLayout>
  );
};

export default TodayPage;
